import { RayCaster } from "../controls";
import { setPointScale } from '../utilities';
import earcut from 'earcut';
import {
  BufferGeometry,
  Color,
  DoubleSide,
  Face3,
  Geometry,
  Line,
  LineBasicMaterial,
  LineDashedMaterial,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Scene,
  SphereGeometry,
  Triangle,
  Vector3
} from "three";
import {
  pointInPolygon,
  checkValidPolygon
} from "../utilities/polygon";
import { textToColor } from "./categories";
import {
  CameraImage,
  CameraLoadOptions,
  ImagePixel,
  PanoramicImage,
  PlanarImage
} from "../cameras/cameras";
import { ImageLabelTools } from ".";
import {
  annotationFromPolygon,
  CocoImage,
  ImageLabel,
  LabelCategory,
  sectionsFromIntersection
} from "./image-label";
import {
  booleanContains,
  intersect as turfIntersect,
  point as turfPoint,
  polygon as turfPolygon
} from "@turf/turf";
import { Viewer } from "../main";

class LabelPoint extends Mesh {
  public coordinate: Coordinate;
  public label: SceneLabel;
  public index;

  constructor(geometry, material, coordinate, label, index) {
    super(geometry, material);
    this.coordinate = coordinate;
    this.label = label;
    this.index = index;
  }
}

class LabelPolygon extends Mesh {
  public label: SceneLabel;

  constructor(geometry, material, label) {
    super(geometry, material);
    this.label = label;
  }
}



class Coordinate {
  private camera: CameraImage;
  public pixel: ImagePixel;
  public _direction: Vector3;
  public _position: Vector3;

  constructor(pixel, camera) {
    this.camera = camera;
    this.updateValues(pixel);
  }

  updateValues(pixel) {
    this.pixel = pixel;

    // Reset direction/position values
    this._direction = null;
    this._position = null;
  }

  get direction() {
    if (this._direction === null) {
      this._direction = this.camera
        .pixelToDirection(this.pixel);
    }

    return this._direction;
  }

  get position() {
    if (this._position === null) {
      let cameraPos = this.camera.position.viewer;
      this._position =  new Vector3()
        .copy(this.direction)
        .multiplyScalar(100)
        .add(cameraPos);
    }

    return this._position;
  }
}

class SceneLabel extends ImageLabel {
  public camera: CameraImage;
  public category: SceneCategory
  public _imageWidth = null;
  public _imageHeight = null;
  public coordinates: Coordinate[] = [];
  public minCoordinates = 3;
  private standardMeshes = [];
  private opacityMeshes = [];
  private pointGeometry: SphereGeometry;
  private pointMaterial: MeshBasicMaterial;
  private lineGeometry: BufferGeometry;
  private lineMaterial: LineBasicMaterial;
  private lineDashedMaterial: LineDashedMaterial;
  private polygonMaterial: MeshBasicMaterial;
  private minPixelDistance = 5;
  private minPoleAngle = 25;

  constructor(category: SceneCategory, camera: CameraImage) {
    super();
    this.category = category;
    this.pointGeometry = new SphereGeometry(0.05, 20, 20);
    this.lineGeometry = new BufferGeometry();
    this.updateMaterials();
    this.setCamera(camera);
  }

  get hasEnoughPoints() {
    return this.numPoints >= this.minCoordinates;
  }

  get numPoints() {
    return this.coordinates.length;
  }

  get coords() {
    /** Used for polygon simplification */
    return this.coordinates.map(x => [x.pixel.u, x.pixel.v]);
  }

  get positions() {
    return this.coordinates.map(x => x.position);
  }

  get directions() {
    return this.coordinates.map(x => x.direction);
  }

  get pixels() {
    return this.coordinates.map(x => x.pixel);
  }

  get viewer() : Viewer {
    return this.category.sceneLabels.viewer;
  }

  get standardScene() {
    return this.category.sceneLabels.standardScene;
  }

  get opacityScene() {
    return this.category.sceneLabels.opacityScene;
  }

  get standardColor() {
    return this.category.color(this.highlighted);
  }

  get darkenedColor() {
    let dark = new Color()
      .copy(this.category.color(this.highlighted))
      .multiplyScalar(0.75);

    return dark;
  }

  get imageWidth() {
    return this._imageWidth
      ? this._imageWidth
      : this.camera.imageWidth;
  }

  get imageHeight() {
    return this._imageHeight
      ? this._imageHeight
      : this.camera.imageHeight;
  }

  setCamera(camera) {
    this.camera = camera;
  }

  setKnownImageSize(width, height) {
    this._imageWidth = width;
    this._imageHeight = height;
  }

  updateMaterials() {
    this.pointMaterial = new MeshBasicMaterial({
      color: this.standardColor
    });

    this.lineMaterial = new LineBasicMaterial({
      color: this.darkenedColor
    });

    this.lineDashedMaterial = new LineDashedMaterial({
      color: this.darkenedColor,
      dashSize: 1.5,
      gapSize: 2.5,
    });

    this.polygonMaterial = new MeshBasicMaterial({
      color: this.standardColor,
      side: DoubleSide
    });
  }

  removePoint() {
    this.coordinates.pop();
  }

  removeSpecificPoint(index) {
    this.coordinates.splice(index, 1);
    this.highlighted = false;
    this.updateMaterials();
    this.draw();
  }

  /** @private */
  drawPoints(temporaryPoint) {
    let positions = this.positions;

    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.position];
    }

    positions.forEach((position, index) => {
      const coordinate = this.coordinates[index];
      const mesh = new LabelPoint(this.pointGeometry,
        this.pointMaterial, coordinate, this, index);

      mesh.position.copy(position);

      this.standardScene.add(mesh);
      this.standardMeshes.push(mesh);
      setPointScale(this.viewer, mesh);
    });
  }

  /** @private */
  drawLines(temporaryPoint) {
    let positions = this.positions;
    if (positions.length === 0) {
      return;
    }

    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.position];
    }

    let corners = [...positions, positions[0]];
    this.lineGeometry.setFromPoints(corners);
    this.lineGeometry.computeBoundingSphere();

    let material = this.completed
      ? this.lineMaterial
      : this.lineDashedMaterial;

    const line = new Line(this.lineGeometry, material);

    if (!this.completed) {
      line.computeLineDistances();
    }

    this.standardMeshes.push(line);
    this.standardScene.add(line);
  }

  /** @private */
  drawPolygons(temporaryPoint: Coordinate) {
    let positions = this.positions;
    let directions = this.directions;
    let pixels = this.pixels;

    if (temporaryPoint && !this.completed) {
      pixels = [...pixels, temporaryPoint.pixel];
      positions = [...positions, temporaryPoint.position];
      directions = [...directions, temporaryPoint.direction];
    }

    if (positions.length < this.minCoordinates) {
      return;
    }

    let coordinates3d = this.camera instanceof PanoramicImage
      ? this.camera.directionsToPlanar(directions)
      : pixels.map(x => new Vector3(x.u, x.v, 0.0));

    let oldState = this.valid;

    // Check for valid polygon
    let coordinates2d = coordinates3d.map(x => [x.x, x.y]);
    this.valid = checkValidPolygon(coordinates2d);

    // Panoramic images arent valid at the poles
    if (this.camera instanceof PanoramicImage) {
      this.valid = this.valid && !this.polygonAtPoles(directions);
    }

    if (this.valid !== oldState) {
      this.category.setCategories();
    }

    if (!this.valid) {
      return;
    }

    // Calculate triangle mesh indices
    let coordinatesFlat = coordinates2d.flat();
    let indices = earcut(coordinatesFlat, null, 2);

    // Deviation must be nearly zero to be valid
    let deviation = earcut.deviation(coordinatesFlat, null, 2, indices);
    if (deviation > 1E-6) {
      return;
    }

    let numTriangles = indices.length / 3;
    for (var i = 0; i < numTriangles; i++) {
      let geom = new Geometry();

      let triangle = new Triangle(
        coordinates3d[indices[3 * i]],
        coordinates3d[indices[3 * i + 1]],
        coordinates3d[indices[3 * i + 2]]
      );

      // Remove invalid triangles
      let center = new Vector3();
      triangle.getMidpoint(center);
      let valid = pointInPolygon(center, coordinates3d);
      if (!valid) {
        continue;
      }

      geom.vertices.push(
        positions[indices[3 * i]],
        positions[indices[3 * i + 1]],
        positions[indices[3 * i + 2]]
      );
      let face = new Face3(0, 1, 2);
      geom.faces.push(face);

      let mesh = new LabelPolygon(geom, this.polygonMaterial, this);
      this.opacityMeshes.push(mesh);
      this.opacityScene.add(mesh);
    }
  }

  draw(temporaryPoint=null) {
    this.clear();

    if (!this.drawable) {
      return;
    }

    if (this.category.currentCamera !== this.camera) {
      return;
    }

    this.drawPoints(temporaryPoint);
    this.drawLines(temporaryPoint);
    this.drawPolygons(temporaryPoint);
  }

  addPoint(pixel: ImagePixel | [], checkDuplicate=true) {
    const newPoint = new Coordinate(pixel, this.camera);
    const oldPoint = this.coordinates[this.coordinates.length - 1];

    if (checkDuplicate && oldPoint) {
      // Check for distance from previous point
      let dU = newPoint.pixel.u - oldPoint.pixel.u;
      let dV = newPoint.pixel.v - oldPoint.pixel.v;
      let distance = Math.sqrt(dU**2 + dV**2);

      if (distance < this.minPixelDistance) {
        return true;
      }
    }

    this.coordinates.push(newPoint);
    return false;
  }

  polygonAtPoles(coordinates): boolean {
    // Check each point to make sure the phi angle is safe
    let pointsAtPole = false;
    coordinates.forEach(x => {
      let phi = MathUtils.radToDeg(Math.acos(x.z));
      let minAngle = phi < this.minPoleAngle;
      let maxAngle = phi > (180 - this.minPoleAngle);

      pointsAtPole = pointsAtPole || minAngle;
      pointsAtPole = pointsAtPole || maxAngle;
    });

    if (pointsAtPole) {
      return true;
    }

    // Check to see if the polygon surrounds the pole
    coordinates = coordinates.map(x => [x.x, x.y]);
    let polyPointRing = [...coordinates, coordinates[0]];

    return booleanContains(
      turfPolygon([polyPointRing]),
      turfPoint([0, 0])
    );
  }

  getCameraBestFit(): CameraLoadOptions {
    // Calculate average direction vector
    let average = new Vector3();
    this.directions.forEach(x => average.add(x));
    average.divideScalar(this.directions.length);

    let fovRequired = this.directions.map(direction => {
      let angle = direction.angleTo(average);
      return MathUtils.radToDeg(angle) * 2.0;
    });

    // Best fit field of view has as small buffer added
    let fov = Math.max(...fovRequired) * 1.10;

    return {fov, direction: average};
  }

  clear() {
    this.standardMeshes.forEach(mesh => {
      this.standardScene.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    });

    this.opacityMeshes.forEach(mesh => {
      this.opacityScene.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    });

    this.standardMeshes = [];
    this.opacityMeshes = [];
  }

  zoom() {
    // The camera must be loaded before we are able to
    // calculate the best fit position, so we calculate
    // these values in the loaded callback
    const cameraID = this.camera.id;

    // Load camera and set position in callback
    this.viewer.setCameraVisible(cameraID, {
      onLoaded: () => {
        let opts = this.getCameraBestFit();
        this.viewer.setCameraVisible(cameraID, opts);
      }
    });
  }

  setHighlighted(highlight) {
    if (highlight === this.highlighted) {
      return;
    }

    this.highlighted = highlight;
    this.updateMaterials();
    this.draw();
  }
}

class SceneCategory extends LabelCategory {
  public sceneLabels: SceneLabelTools;
  public labels: SceneLabel[] = [];

  constructor(sceneLabels, name) {
    super(name);
    this.sceneLabels = sceneLabels;
  }

  get currentCamera() {
    return this.sceneLabels.currentCamera;
  }

  color(highlight=false) : Color {
    let color = `rgba(${Math.ceil(255)}, ${Math.ceil(255)}, ${Math.ceil(255)})`;
    if (!highlight) {
      color = textToColor(this.name);
    }
    return new Color(color);
  }

  setCategories() {
    this.sceneLabels.parent.setCategories();
  }

  updateColor() {
    this.labels.forEach(label => {
      label.updateMaterials();
    });

    this.sceneLabels.drawAll();
  }

  addNewLabel(drawLabel) {
    let label = new SceneLabel(this, this.currentCamera);
    label.setDrawable(drawLabel);
    this.labels.push(label);

    return label;
  }

  deleteUnfinishedLabel() {
    let numberOfLabels = this.labels.length;
    if (numberOfLabels === 0) {
      return;
    }

    if (!this.labels[numberOfLabels - 1].completed) {
      this.labels.pop();
    }
  }
}

export class SceneLabelTools {
  public type = "scene";
  public parent: ImageLabelTools;
  public standardScene: Scene;
  public opacityScene: Scene;
  private setViewerTooltipText;
  public enabled = false;
  public opacity = 0.2;
  public contextMenuOpen = false;
  private contextMenuClick;
  private polygonHover = false;

  private temporaryPoint: Coordinate;
  public movingEndpoint = false;
  public draggable = false;
  private dragObject: LabelPoint = null;

  private startTooltip = "Click to start drawing polygon";

  private dragTooltip = `Click and hold to move vertex
                        <br>Right Click to select point`;

  private highlightTooltip = `Right Click to select label
                              <br>CTRL + Right Click to select multiple labels`;

  constructor(parent: ImageLabelTools, props) {
    const {setViewerTooltipText, contextMenuClick} = props;

    this.parent = parent;
    this.standardScene = new Scene();
    this.opacityScene = new Scene();
    this.setViewerTooltipText = setViewerTooltipText;
    this.contextMenuClick = contextMenuClick;

    window.addEventListener('keydown', event => {
      this.onKeyDown(event);
    }, false);
  }

  get categories() {
    return this.parent.categories
      .filter(x => x instanceof SceneCategory) as SceneCategory[];
  }

  get viewer() {
    return this.parent.viewer;
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get currentCamera() {
    let current = this.cameraList.current;
    if (this.viewer.orbitState || !current) {
      return null;
    }

    return current;
  }

  get drawing() {
    let label = this.activeLabel;
    if (!label || label.completed) {
      return false;
    }

    if (label.coordinates.length === 0) {
      return false;
    }

    return true;
  }

  get activeLabel() {
    let selected = null;
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.selected) {
          selected = label;
        }
      });
    });

    return selected;
  }

  get moreTooltip() {
    return `
      Click to add more points (${this.activeLabel.numPoints})
      <br>Delete Key to remove last point
      ${(this.activeLabel.numPoints > 1) ? "<br>Double-click to finish" : ""}
      <br>Right-click to remove label
    `;
  }

  get invalidTooltip() {
    let isPanoramic = this.activeLabel.camera instanceof PanoramicImage;
    let extraText = isPanoramic ? "or closeness to poles" : "";

    return `
      Polygon is invalid due to self-intersection ${extraText}
      <br>Click to add more points (${this.activeLabel.numPoints})
      ${(this.activeLabel.numPoints > 1) ? "<br>Double-click to finish" : ""}
      <br>Right-click to remove label
    `;
  }

  get visibleCameras() {
    let visited = new Set();
    let cameras = [];

    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (!label.valid) {
          return;
        }

        let camera = label.camera;
        let cameraID = camera.id;

        if (visited.has(cameraID)) {
          return;
        }

        visited.add(cameraID);
        cameras.push(camera);
      });
    });

    return cameras;
  }

  get activeImagePaths() {
    return this.visibleCameras.map(image => image.path);
  }

  get loadedImagePaths() {
    let imageList = this.viewer.cameraList;
    return imageList.cameras.map(image => image.path);
  }

  setState(state) {
    this.enabled = state;
    this.resetToolTipText();
  }

  getNewCategory(name) {
    return new SceneCategory(this, name);
  }

  getClickedDirection(event) {
    const raycaster = new RayCaster(this.viewer, event);
    const direction = raycaster.vectorFromEvent(event);
    return direction;
  }

  getImagePixel(event): [boolean, ImagePixel] {
    const direction = this.getClickedDirection(event);
    const [valid, pixel] = this.currentCamera
      .directionToPixel(direction);

    return [valid, pixel];
  }

  deletePoint() {
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          label.removePoint();
          this.setToolTipText();
        }
      })
    );

    this.parent.setCategories();
  }

  deleteSpecificPoint(id) {
    this.categories.forEach(category =>
      category.labels.forEach(label => {

        if (label.highlighted && label.hasEnoughPoints) {
          label.removeSpecificPoint(id);
          this.parent.unHighlight(label.id);
        }
      })
    );
    this.contextMenuOpen = false;
    this.parent.setCategories();
  }

  highlightLabel(event) {
    if (this.drawing) {
      return;
    }

    let targets = [
      ...this.standardScene.children.filter(x => (x instanceof LabelPoint)),
      ...this.opacityScene.children.filter(x => (x instanceof LabelPolygon))
    ];

    if (targets.length === 0) {
      return;
    }

    let raycaster = new RayCaster(this.viewer, event);
    let intersectedTargets = raycaster.intersectObjects(targets);
    if (intersectedTargets.length === 0) {
      return;
    }

    let selected = intersectedTargets[0].object;
    let label = selected.label;
    label.setHighlighted(!label.highlighted);
    this.parent.setCategories();

    return selected;
  }

  mouseClick(event) {

    if (!this.currentCamera) {
      return;
    }

    let label = this.activeLabel;
    if (!label) {
      return;
    }

    let [valid, point] = this.getImagePixel(event);
    if (!valid) {
      return;
    }

    let duplicatePoint = label.addPoint(point);
    if (duplicatePoint && label.hasEnoughPoints) {
      this.completeLabel(label);
      return;
    }

    this.setToolTipText();
    this.resetTemporaryPoint();
    this.drawAll();
  }

  completeLabel(label) {
    this.resetTemporaryPoint();
    this.parent.completeLabel(label.id);
    this.markLastPointDraggable(label);
  }

  markLastPointDraggable(label) {
    let labelPoints = label.standardMeshes
      .filter(x => x instanceof LabelPoint);

    let lastPoint = labelPoints[labelPoints.length - 1];
    this.setDragObject(lastPoint);
  }

  cancelLabel() {
    let label = this.activeLabel;
    if (!label) {
      return;
    }

    this.parent.deleteLabel(label.id);
    this.setToolTipText();
    this.drawAll();
  }

  checkPointSelected(event) {
    if (this.drawing) {
      return;
    }

    let targetPoly = this.opacityScene.children
      .filter(x => (x instanceof LabelPolygon));

    let targets = this.standardScene.children
      .filter(x => x instanceof LabelPoint);

    targets = targets.concat(targetPoly);

    let dragObject = null;
    let polygonHover = false;

    if (targets.length > 0) {
      let raycaster = new RayCaster(this.viewer, event);
      let objects = raycaster.intersectObjects(targets);
      objects = objects.map(x => x.object);

      let polygons = objects
        .filter(x => x instanceof LabelPolygon);

      let points = objects
        .filter(x => x instanceof LabelPoint);

      let draggable = points.length > 0;
      dragObject = draggable ? points[0] : null;

      polygonHover = (polygons.length > 0);
    }

    this.setPolygonHover(polygonHover);
    this.setDragObject(dragObject);
  }

  updateDraggablePoint(event) {
    let coordinate = this.dragObject.coordinate;
    let [valid, point] = this.getImagePixel(event);
    if (!valid) {
      return;
    }

    coordinate.updateValues(point);
    this.drawAll();
  }

  setDragObject(dragObject) {
    this.dragObject = dragObject;

    if (dragObject) {
      this.setPointGrabTooltip();
    }
  }

  setPolygonHover(hovering) {
    this.polygonHover = hovering;
  }

  setToolTipText() {
    if (!this.activeLabel) {
      this.resetToolTipText();
      return;
    }

    let text = "";
    if (this.drawing) {
      text = this.activeLabel.valid
        ? this.moreTooltip
        : this.invalidTooltip;
    } else {
      if (this.currentCamera) {
        text = this.startTooltip;
      }
    }

    this.setViewerTooltipText(text);
  }

  resetToolTipText() {
    this.setViewerTooltipText("");
  }

  setPointGrabTooltip() {
    this.setViewerTooltipText(this.dragTooltip);
  }

  setPolygonHoverTooltip() {
    this.setViewerTooltipText(this.highlightTooltip);
  }

  resetTemporaryPoint() {
    this.temporaryPoint = null;
  }

  highlightOnClick(event) {

    let labelHighlighted = false;
    const noneHighlighted = (this.parent.numHighlighted() === 0);
    const oneHighlighted = (this.parent.numHighlighted() === 1);

    if (noneHighlighted && !event.ctrlKey && !this.contextMenuOpen) {
      let highlightedFeature =  this.highlightLabel(event);

      if (highlightedFeature) {
        labelHighlighted = true;
        let isPoint = (highlightedFeature instanceof LabelPoint) ? highlightedFeature.index : null;
        let highlightedLabel = highlightedFeature.label;
        this.contextMenuClick(event, isPoint, highlightedLabel.id);
        this.contextMenuOpen = true;
      }
    }

    else if (oneHighlighted && !event.ctrlKey && !this.contextMenuOpen) {
      this.parent.unHighlightAll();
      let highlightedFeature =  this.highlightLabel(event);

      if (highlightedFeature) {
        labelHighlighted = true;
        let isPoint = (highlightedFeature instanceof LabelPoint) ? highlightedFeature.index : null;
        let highlightedLabel = highlightedFeature.label;
        this.contextMenuClick(event, isPoint, highlightedLabel.id);
        this.contextMenuOpen = true;
      }
    }

    else if (event.ctrlKey && !this.contextMenuOpen) {
      let highlightedFeature = this.highlightLabel(event);

      if (highlightedFeature) {
        labelHighlighted = true;
      }
    }

    return labelHighlighted;
  }

  onMouseMove(event, mouse = null) {
    const mouseMoved = mouse ? mouse.movement : false;
    const mouseDown = mouse ? mouse.down : false;
    const isLeftClick = mouse ? mouse.button === 0 : false;

    this.resetTemporaryPoint();
    this.movingEndpoint = false;

    if (!this.enabled) {
      return;
    }

    if (!mouseMoved && !mouseDown) {
      this.checkPointSelected(event);
    }

    if (mouse === null) {
      return;
    }

    if (this.drawing) {
      let [valid, point] = this.getImagePixel(event);
      if (!valid) {
        return;
      }

      this.temporaryPoint = new Coordinate(point,
        this.currentCamera);

      this.drawAll();
    }

    if (this.dragObject) {
      this.setPointGrabTooltip();
      if (mouseDown) {
        if (isLeftClick) {
          this.updateDraggablePoint(event);
        }
        this.movingEndpoint = true;
        return;
      }
    }
    else if (this.polygonHover) {
      this.setPolygonHoverTooltip();
    }
    else {
      this.setToolTipText();
    }
  }

  onKeyDown(event) {
    if (event.which === 13) {
      let label = this.activeLabel;
      if (!label) {
        return;
      }

      if (label.selected){
        this.parent.completeLabel(label.id);
      }
    }
  }

  drawAll() {
    this.standardScene.clear();
    this.opacityScene.clear();

    if (this.viewer.orbitState) {
      return;
    }

    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.draw(this.temporaryPoint);
      });
    });
  }

  /** @private */
  calculatePlanarSections(polyPointRing, imageWidth, imageHeight) {
    let sections = [];

    let imageBorders = [
      [0, 0],
      [0, imageHeight-1],
      [imageWidth-1, imageHeight-1],
      [imageWidth-1, 0],
      [0, 0]
    ];

    let intersection = turfIntersect(
      turfPolygon([polyPointRing]),
      turfPolygon([imageBorders]),
    );

    let intersectionData = sectionsFromIntersection(intersection);
    intersectionData.forEach((arr) => {
      sections.push(arr);
    });

    return sections;
  }

  /** @private */
  calculatePanoramicSections(polyPointRing, image: PanoramicImage, imageWidth, imageHeight) {
    let sections = [];
    let horizontalShift = this.getHorizontalShift(
      polyPointRing, imageWidth);

    // Add horizontal shift
    polyPointRing = polyPointRing.map(x=> {
      return [(x[0] + horizontalShift) % imageWidth, x[1]];
    });

    // Interpolate pixels for smooth curve in pixel space
    polyPointRing = image.interpolatePixels(
      polyPointRing, imageWidth, imageHeight);

    // Compute sections for left polygon
    let imageBordersLeft = [
      [-9999, 0],
      [-9999, imageHeight],
      [horizontalShift-0.05, imageHeight],
      [horizontalShift-0.05, 0],
      [-9999, 0]
    ];

    let intersectionLeft = turfIntersect(
      turfPolygon([polyPointRing]),
      turfPolygon([imageBordersLeft]),
    );

    let intersectionDataLeft = sectionsFromIntersection(intersectionLeft);
    intersectionDataLeft.forEach((arr) => {
      sections.push(arr);
    });

    // Compute sections for right polygon
    let imageBordersRight = [
      [horizontalShift, 0],
      [horizontalShift, imageHeight],
      [9999, imageHeight],
      [9999, 0],
      [horizontalShift, 0]
    ];

    let intersectionRight = turfIntersect(
      turfPolygon([polyPointRing]),
      turfPolygon([imageBordersRight]),
    );

    let intersectionDataRight = sectionsFromIntersection(intersectionRight);
    intersectionDataRight.forEach((arr) => {
      sections.push(arr);
    });

    // Subtract shift from sections
    sections = sections.map(points => {
      return points.map(point => {
        let y = point[1];
        let x = point[0] - horizontalShift;
        x = (x + imageWidth) % imageWidth;

        return [x, y];
      });
    });

    return sections;
  }

  /** @private */
  getHorizontalShift(polyPointRing, imageWidth) {
    let minSpread = Infinity;
    let horizontalShift = 0;

    for (var i=0;i<imageWidth;i++) {
      // eslint-disable-next-line no-loop-func
      let x = polyPointRing.map(x=> {
        return (x[0] + i) % imageWidth;
      });

      let spread = Math.max(...x) - Math.min(...x);
      if (spread < minSpread) {
        horizontalShift = i;
        minSpread = spread;
      }
    }

    return horizontalShift;
  }

  polygonInImage(label: SceneLabel) {
    let sections = [];

    let pixels = label.pixels;
    let image = label.camera;

    let imageWidth = label.imageWidth;
    let imageHeight = label.imageHeight;

    let coordinates = pixels.map(x => [x.u, x.v]);
    let polyPointRing = [...coordinates, coordinates[0]];

    if (image instanceof PlanarImage) {
      sections = this.calculatePlanarSections(
        polyPointRing, imageWidth, imageHeight);
    } else if (image instanceof PanoramicImage) {
      sections = this.calculatePanoramicSections(
        polyPointRing, image, imageWidth, imageHeight);
    } else {
      return sections;
    }

    return sections;
  }

  pointsToPixels(points): ImagePixel[] {
    return points.map(x => {
      return {u: x[0], v: x[1]} as ImagePixel;
    });
  }

  getCameraSize(image) {
    let width = 0;
    let height = 0;

    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.camera === image) {
          width = label.imageWidth;
          height = label.imageHeight;
        }
      });
    });

    return {width, height};
  }

  jsonImageObj() {
    let images = this.visibleCameras.map((image, imageIndex) => {
      const {width, height} = this.getCameraSize(image);
      return {
        height: height,
        width: width,
        id: imageIndex,
        file_name: image.name,
      } as CocoImage;
    });

    return images;
  }

  jsonLabelsObj() {
    let labelIndex = 1;
    let mergeIndex = 1;
    let annotations = [];

    this.categories.forEach((category, categoryID) => {
      category.labels.forEach(label => {
        if (!label.valid) {
          return;
        }

        const imageID = this.visibleCameras.indexOf(label.camera);
        const polygonsInside = this.polygonInImage(label);

        let mergeable = polygonsInside.length > 1;

        polygonsInside.forEach((polygon, index) => {
          let annotation = annotationFromPolygon(
            polygon,
            labelIndex,
            categoryID,
            imageID,
            index,
            mergeable ? mergeIndex : null
          );

          annotations.push(annotation);
          labelIndex++;
        });

        if (mergeable) {
          mergeIndex++;
        }
      });
    });

    return [annotations, []];
  }

  update(changeDetector) {
    const update = changeDetector.changed;
    if (!update) {
      return;
    }

    if (changeDetector.cameraChanged || changeDetector.orbitChanged) {
      this.categories.forEach(category => {
        // Remove incomplete observation if camera changed
        category.deleteUnfinishedLabel();
        this.resetTemporaryPoint();
      });

      this.drawAll();
      return;
    }

    this.standardScene.children.forEach(mesh => {
      if (mesh instanceof LabelPoint) {
        setPointScale(this.viewer, mesh);
      }
    });
  }
}

